import { type FC, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import * as Sentry from '@sentry/react'
import { Modal } from '../../../../../components/Modal/Modal'
import { Spinner } from '../../../../../components/Spinner/Spinner'
import { Button } from '../../../../../components/Button/Button'
import { type SchedulingUser } from '../../../../../models/Scheduling'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetSchedulingUsersQuery } from '../../../../../store/scheduling/scheduling'

const PAGE_SIZE = 5

interface UsedByModalProps {
  eventTypeId: string
  eventTypeSlug: string
  onClose: () => void
}

export const UsedByModal: FC<UsedByModalProps> = ({ eventTypeId, eventTypeSlug, onClose }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: users, isLoading } = useGetSchedulingUsersQuery({ accountId: authenticatedUser.accountId, eventTypeId })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (users ?? []).filter((user) => !term || (user.name ?? user.email ?? '').toLowerCase().includes(term))
  }, [users, search])

  const total = filtered.length
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)
  const visible = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  const resolveBookingUrl = (user: SchedulingUser): string | null => (user.publicUrl ? `${user.publicUrl}/${eventTypeSlug}` : null)

  useEffect(() => {
    if ((users ?? []).some((user) => !user.publicUrl)) {
      Sentry.captureException(new Error('Scheduling: user is missing publicUrl; cannot build a booking URL in the users-with-event-type modal'))
    }
  }, [users])

  return (
    <Modal
      show
      size='lg'
      onHide={onClose}
      header={
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h5 className='mb-0'>Users with this event type</h5>
          <span className='paragraph-small zocks-text-gray-600'>Each user has their own booking link.</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <Form.Control
            type='search'
            placeholder='Search'
            value={search}
            aria-label='Search users'
            onChange={(event) => {
              setSearch(event.currentTarget.value)
              setPage(0)
            }}
          />
          {isLoading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner />
            </div>
          ) : (
            <>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='paragraph-small zocks-text-gray-600'>
                  Showing {visible.length} out of {total}
                </span>
                <div className='d-flex align-items-center zocks-gap-0.5'>
                  <Button
                    variant='grayGhost'
                    className='p-1'
                    aria-label='Previous page'
                    disabled={page <= 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    <i className='bi bi-chevron-left' />
                  </Button>
                  <Button
                    variant='grayGhost'
                    className='p-1'
                    aria-label='Next page'
                    disabled={page >= maxPage}
                    onClick={() => setPage((current) => Math.min(maxPage, current + 1))}
                  >
                    <i className='bi bi-chevron-right' />
                  </Button>
                </div>
              </div>
              {visible.map((user) => {
                const bookingUrl = resolveBookingUrl(user)
                return (
                  <div key={user.id} className='d-flex align-items-center justify-content-between gap-3 py-2 border-top zocks-border-color-gray-200'>
                    <div className='d-flex flex-column'>
                      <span className='bold'>
                        {user.name ?? user.email ?? 'User'}
                        {user.id === authenticatedUser.id ? ' (Me)' : ''}
                      </span>
                      {bookingUrl ? (
                        <span className='paragraph-small zocks-text-gray-600'>{bookingUrl.replace(/^https?:\/\//, '')}</span>
                      ) : (
                        <span className='paragraph-small zocks-text-danger'>Booking link unavailable</span>
                      )}
                    </div>
                    <div className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0'>
                      <span className='badge rounded-pill zocks-bg-color-success-background zocks-text-success'>Shown on booking page</span>
                      {bookingUrl ? (
                        <a
                          href={bookingUrl}
                          target='_blank'
                          rel='noreferrer'
                          aria-label={`Open ${user.name ?? 'user'} booking page`}
                          className='zocks-text-gray-600'
                        >
                          <i className='bi bi-box-arrow-up-right' />
                        </a>
                      ) : null}
                    </div>
                  </div>
                )
              })}
              {total === 0 ? <span className='paragraph-small zocks-text-gray-600'>No users have this event type yet.</span> : null}
            </>
          )}
        </div>
      }
      showFooterActions
      hideSecondaryAction
      footerPrimaryActionTitle='Close'
      onFooterPrimaryAction={onClose}
    />
  )
}
